/**
 * The patient list's request, built from one canonical filter state (ADR-012).
 *
 * Values within a category combine with OR and categories combine with AND.
 * The server does both, and it filters and counts before it pages: every list
 * here is paged on the server, so the browser can never intersect pages.
 */

import type { FiltersState } from '@components/ui/FilterMenu/types';

/** Filter field ids. `FiltersState` is keyed by these. */
export const LIST_FIELDS = {
  status: 'status',
  payers: 'payers',
  coverage: 'coverage',
  missing: 'missing',
} as const;

export type ListCategory = 'payers' | 'coverage' | 'missing';

const CATEGORY_PARAMS: Record<ListCategory, keyof Omit<AppliedFilters, 'activity_status' | 'q'>> = {
  payers: 'payer_uuid',
  coverage: 'coverage_bucket',
  missing: 'patient_requirement_definition_id',
};

export type CoverageBucket = 'insurance' | 'needs_review' | 'self_pay' | 'no_coverage';

export const COVERAGE_OPTIONS: { value: CoverageBucket; label: string }[] = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'self_pay', label: 'Self-pay' },
  { value: 'no_coverage', label: 'No coverage' },
];

/** What the server says it applied. Present on every `/patient/list` response once GDN-4120 ships. */
export interface AppliedFilters {
  activity_status: 'all' | 'active' | 'inactive';
  payer_uuid: string[];
  coverage_bucket: string[];
  patient_requirement_definition_id: string[];
  q: string | null;
}

export const selectedValues = (filters: FiltersState, field: string): string[] => {
  const value = filters[field];
  return value?.kind === 'multiSelect' ? value.values : [];
};

/** Adds or removes one value, dropping the category when it empties. */
export const toggleValue = (filters: FiltersState, field: string, value: string): FiltersState => {
  const current = selectedValues(filters, field);
  const values = current.includes(value)
    ? current.filter(item => item !== value)
    : [...current, value];
  if (values.length) return { ...filters, [field]: { kind: 'multiSelect', values } };
  const { [field]: _removed, ...rest } = filters;
  return rest;
};

export const activityStatus = (filters: FiltersState): 'active' | 'inactive' | null => {
  const status = filters[LIST_FIELDS.status];
  if (status?.kind !== 'boolean') return null;
  return status.value ? 'active' : 'inactive';
};

const hasCategoryFilter = (filters: FiltersState): boolean =>
  (Object.keys(CATEGORY_PARAMS) as ListCategory[]).some(
    category => selectedValues(filters, category).length > 0,
  );

/**
 * A request the pre-GDN-4120 server cannot answer correctly. It reads only
 * the first value of a repeated parameter, lets payer silently win over
 * coverage, and ignores `q` on `/patient/list`. Anything else means the same
 * on both servers.
 */
export const isCombinedRequest = (filters: FiltersState, search: string): boolean =>
  (Object.keys(CATEGORY_PARAMS) as ListCategory[]).some(
    category => selectedValues(filters, category).length > 1,
  ) ||
  (selectedValues(filters, 'payers').length > 0 &&
    selectedValues(filters, 'coverage').length > 0) ||
  (search.length > 0 && hasCategoryFilter(filters));

export interface ListRequest {
  filters: FiltersState;
  search: string;
  /** Zero-based. */
  page: number;
  pageSize: number;
}

/**
 * The endpoint for one request. A plain name search keeps using
 * `/patient/search`, which both servers answer the same way; everything else
 * goes to the composable `/patient/list`.
 */
export const buildListEndpoint = ({ filters, search, page, pageSize }: ListRequest): string => {
  const params = new URLSearchParams();
  const searching = search.length > 0;
  const useSearchRoute = searching && !hasCategoryFilter(filters);
  if (useSearchRoute) params.append('q', search);
  params.append('page', String(page + 1));
  params.append('per_page', String(pageSize));
  const status = activityStatus(filters);
  if (status) params.append('activity_status', status);
  if (!useSearchRoute) {
    (Object.keys(CATEGORY_PARAMS) as ListCategory[]).forEach(category =>
      selectedValues(filters, category).forEach(value =>
        params.append(CATEGORY_PARAMS[category], value),
      ),
    );
    if (searching) params.append('q', search);
  }
  return `${useSearchRoute ? '/patient/search' : '/patient/list'}?${params.toString()}`;
};

// The server echoes UUIDs in canonical lowercase; buckets are lowercase already.
const sameSet = (a: string[], b: string[]): boolean => {
  const left = new Set(a.map(value => value.toLowerCase()));
  const right = new Set(b.map(value => value.toLowerCase()));
  return left.size === right.size && [...left].every(value => right.has(value));
};

/**
 * Whether rows can be shown for this request (ADR-012 invariant 5).
 *
 * A combined request needs the server's echo, and the echo has to match what
 * was asked. An older server sends no echo and would answer with wrong rows,
 * so the list shows an error instead. A request that is not combined means the
 * same on both servers, so a missing echo is fine; a present one still has to
 * agree.
 */
export const appliedFiltersMatch = (
  applied: AppliedFilters | undefined,
  filters: FiltersState,
  search: string,
): boolean => {
  if (!applied) return !isCombinedRequest(filters, search);
  return (
    (applied.activity_status === 'all' ? null : applied.activity_status) ===
      activityStatus(filters) &&
    (Object.keys(CATEGORY_PARAMS) as ListCategory[]).every(category =>
      sameSet(applied[CATEGORY_PARAMS[category]], selectedValues(filters, category)),
    ) &&
    (applied.q ?? '') === search
  );
};

/** Identifies which requirements a set of row columns describes. */
export const requirementKey = (filters: FiltersState): string | null => {
  const ids = selectedValues(filters, 'missing');
  return ids.length ? [...ids].sort().join(',') : null;
};

// ── URL state ────────────────────────────────────────────────────

/** Query-string keys. Short, readable in a shared link, and stable. */
const URL_KEYS: Record<ListCategory, string> = {
  payers: 'payer',
  coverage: 'coverage',
  missing: 'missing',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What the URL holds: filters and page. Ids and enums only.
 *
 * The name search is deliberately NOT here. A URL is copied into analytics
 * (Statsig auto-capture's `page_url`), error reports, and session replay, and
 * a patient's name must not reach any of them. The search travels in history
 * state instead: Back, Forward, and reload restore it, a shared link does not.
 */
export interface UrlView {
  filters: FiltersState;
  /** Zero-based. */
  page: number;
}

export const viewToParams = ({ filters, page }: UrlView): URLSearchParams => {
  const params = new URLSearchParams();
  const status = activityStatus(filters);
  if (status) params.set('status', status);
  (Object.keys(URL_KEYS) as ListCategory[]).forEach(category =>
    selectedValues(filters, category).forEach(value => params.append(URL_KEYS[category], value)),
  );
  if (page > 0) params.set('page', String(page + 1));
  return params;
};

const validValue = (category: ListCategory, value: string): boolean =>
  category === 'coverage'
    ? COVERAGE_OPTIONS.some(option => option.value === value)
    : UUID.test(value);

/**
 * Reads the filters and page back. Anything malformed is dropped rather than
 * sent: the server answers a malformed id with 400, and a link someone
 * truncated should still open a working list.
 */
export const paramsToView = (params: URLSearchParams): UrlView => {
  const filters: FiltersState = {};
  const status = params.get('status');
  if (status === 'active' || status === 'inactive') {
    filters[LIST_FIELDS.status] = { kind: 'boolean', value: status === 'active' };
  }
  (Object.keys(URL_KEYS) as ListCategory[]).forEach(category => {
    const values = [...new Set(params.getAll(URL_KEYS[category]))].filter(value =>
      validValue(category, value),
    );
    if (values.length) filters[category] = { kind: 'multiSelect', values };
  });
  const page = Number.parseInt(params.get('page') ?? '', 10);
  return { filters, page: Number.isFinite(page) && page > 1 ? page - 1 : 0 };
};

/** The filters a request may carry: requirement ids only for a user allowed them. */
export const requestableFilters = (
  filters: FiltersState,
  canFilterRequirements: boolean,
): FiltersState => {
  if (canFilterRequirements || !filters[LIST_FIELDS.missing]) return filters;
  const { [LIST_FIELDS.missing]: _missing, ...rest } = filters;
  return rest;
};
